import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ListChecks,
  LayoutGrid,
  KanbanSquare,
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  Plus,
  Trash2,
  Filter,
  RotateCw,
  RefreshCw,
  LogOut,
  Lock,
  Search,
  ChevronRight,
  ChevronDown,
  LayoutDashboard,
  ArrowRight,
  Shield,
  CreditCard,
  Wrench,
  Sparkles,
  Trophy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  isPlanUnlocked,
  tryUnlockPlan,
  lockPlan,
  listPlanTasks,
  createPlanTask,
  updatePlanTask,
  deletePlanTask,
  seedPlanTasks,
  resetPlanTasks,
  syncPlanTasks,
} from "@/lib/planApi";

const ESTADOS = [
  { value: "pendiente", label: "Pendiente", icon: Circle, color: "text-zinc-500" },
  { value: "en_curso", label: "En curso", icon: Clock, color: "text-blue-600" },
  { value: "bloqueado", label: "Bloqueado", icon: AlertCircle, color: "text-amber-600" },
  { value: "hecho", label: "Hecho", icon: CheckCircle2, color: "text-emerald-600" },
];

const PRIORIDADES = [
  { value: "alta", label: "Alta", className: "bg-rose-100 text-rose-700 border-rose-200" },
  { value: "media", label: "Media", className: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "baja", label: "Baja", className: "bg-zinc-100 text-zinc-700 border-zinc-200" },
];

const OVERVIEW_KEY = "__resumen__";

const SECTION_ORDER = [
  "Etapas completadas",
  "Modulo de pagos",
  "Pre-produccion",
  "Deuda tecnica",
  "Backlog de producto",
];

// Metadatos por sección: icono + 1 frase de resumen + orden recomendado.
const SECTION_META = {
  "Etapas completadas": {
    icon: Trophy,
    label: "Etapas completadas",
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    resumen: "Hitos ya cerrados: infraestructura propia, login, aislamiento entre usuarios, refuerzos y esta misma página.",
  },
  "Modulo de pagos": {
    icon: CreditCard,
    label: "Módulo de pagos",
    color: "text-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-200",
    resumen: "Stripe + suscripciones (Free / Pro / Enterprise) + sistema de créditos. Todo en un bloque.",
    orden: 1,
  },
  "Pre-produccion": {
    icon: Shield,
    label: "Pre-producción",
    color: "text-rose-600",
    bg: "bg-rose-50",
    border: "border-rose-200",
    resumen: "Acciones a tener listas antes del primer cliente real: backups, índices, restringir BD, etc.",
    orden: 2,
  },
  "Deuda tecnica": {
    icon: Wrench,
    label: "Deuda técnica",
    color: "text-zinc-600",
    bg: "bg-zinc-50",
    border: "border-zinc-300",
    resumen: "Refactor y bugs conocidos. No bloquea, se va resolviendo al tocar cada área.",
    orden: 3,
  },
  "Backlog de producto": {
    icon: Sparkles,
    label: "Backlog de producto",
    color: "text-violet-600",
    bg: "bg-violet-50",
    border: "border-violet-200",
    resumen: "Funciones nuevas para más adelante: proyecciones, integraciones bancarias, PDF, gráficos, emails.",
    orden: 4,
  },
};

function PasswordGate({ onUnlock }) {
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (tryUnlockPlan(pwd)) {
      onUnlock();
    } else {
      setError(true);
      setPwd("");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white border border-zinc-200 rounded-lg p-8 shadow-sm"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-md bg-zinc-900 flex items-center justify-center">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight text-zinc-900">
              Finreport / Plan
            </h1>
            <p className="text-xs text-zinc-500">Seguimiento interno</p>
          </div>
        </div>
        <label className="block text-sm font-medium text-zinc-700 mb-2">
          Contraseña
        </label>
        <Input
          type="password"
          value={pwd}
          onChange={(e) => {
            setPwd(e.target.value);
            setError(false);
          }}
          autoFocus
          className={error ? "border-rose-500" : ""}
          data-testid="plan-password-input"
        />
        {error && (
          <p className="text-xs text-rose-600 mt-2">Contraseña incorrecta</p>
        )}
        <Button type="submit" className="w-full mt-5" data-testid="plan-password-submit">
          Entrar
        </Button>
      </form>
    </div>
  );
}

function TaskRow({ task, expanded, onToggleExpand, onUpdate, onDelete }) {
  const estado = ESTADOS.find((e) => e.value === task.estado) || ESTADOS[0];
  const prioridad = PRIORIDADES.find((p) => p.value === task.prioridad) || PRIORIDADES[1];
  const Icon = estado.icon;
  const isDone = task.completado || task.estado === "hecho";
  const hasDetalles = !!(task.detalles && task.detalles.trim());

  const toggleCheck = () => {
    const nowDone = !isDone;
    onUpdate(task.id, {
      completado: nowDone,
      estado: nowDone ? "hecho" : "pendiente",
    });
  };

  return (
    <div className="border-b border-zinc-200">
      <div
        className={`flex items-start gap-3 px-4 py-3 transition-colors ${
          hasDetalles ? "cursor-pointer hover:bg-zinc-50" : "hover:bg-zinc-50"
        }`}
        onClick={(e) => {
          // Solo expandir si clicamos en zona vacía del row (no en controles)
          if (e.target.closest("[data-no-expand]")) return;
          if (hasDetalles) onToggleExpand(task.id);
        }}
      >
        <div data-no-expand className="pt-0.5">
          <Checkbox
            checked={isDone}
            onCheckedChange={toggleCheck}
            data-testid={`task-check-${task.id}`}
          />
        </div>
        <div className="pt-0.5 text-zinc-400">
          {hasDetalles ? (
            expanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )
          ) : (
            <div className="w-4" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`font-medium text-sm ${
                isDone ? "line-through text-zinc-400" : "text-zinc-900"
              }`}
            >
              {task.titulo}
            </span>
            <Badge variant="outline" className={`text-xs ${prioridad.className}`}>
              {prioridad.label}
            </Badge>
            {task.estimacion && (
              <span className="text-xs text-zinc-500 font-mono">⏱ {task.estimacion}</span>
            )}
          </div>
          {task.descripcion && (
            <p className="text-xs text-zinc-600 mt-1 leading-relaxed">
              {task.descripcion}
            </p>
          )}
        </div>
        <div data-no-expand>
          <Select
            value={task.estado}
            onValueChange={(v) => onUpdate(task.id, { estado: v, completado: v === "hecho" })}
          >
            <SelectTrigger className="w-[130px] h-8 text-xs">
              <span className="flex items-center gap-1.5">
                <Icon className={`w-3.5 h-3.5 ${estado.color}`} />
                <SelectValue />
              </span>
            </SelectTrigger>
            <SelectContent>
              {ESTADOS.map((e) => (
                <SelectItem key={e.value} value={e.value} className="text-xs">
                  <span className="flex items-center gap-2">
                    <e.icon className={`w-3.5 h-3.5 ${e.color}`} />
                    {e.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div data-no-expand>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-zinc-400 hover:text-rose-600"
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`¿Borrar "${task.titulo}"?`)) onDelete(task.id);
            }}
            data-testid={`task-delete-${task.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      {expanded && hasDetalles && (
        <div className="bg-zinc-50 border-t border-zinc-200 px-12 py-4">
          <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">
            Detalle técnico
          </div>
          <pre className="text-xs text-zinc-700 font-mono whitespace-pre-wrap leading-relaxed">
            {task.detalles}
          </pre>
        </div>
      )}
    </div>
  );
}

function KanbanCard({ task, expanded, onToggleExpand, onUpdate, onDelete }) {
  const prioridad = PRIORIDADES.find((p) => p.value === task.prioridad) || PRIORIDADES[1];
  const hasDetalles = !!(task.detalles && task.detalles.trim());
  return (
    <div className="bg-white border border-zinc-200 rounded-md hover:border-zinc-400 transition-colors">
      <div
        className={`p-3 ${hasDetalles ? "cursor-pointer" : ""}`}
        onClick={(e) => {
          if (e.target.closest("[data-no-expand]")) return;
          if (hasDetalles) onToggleExpand(task.id);
        }}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-start gap-1.5 min-w-0">
            {hasDetalles && (
              <span className="text-zinc-400 mt-0.5">
                {expanded ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
              </span>
            )}
            <span className="text-sm font-medium text-zinc-900 leading-snug">
              {task.titulo}
            </span>
          </div>
          <div data-no-expand>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 -mt-1 -mr-1 text-zinc-300 hover:text-rose-600"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`¿Borrar "${task.titulo}"?`)) onDelete(task.id);
              }}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
        {task.descripcion && (
          <p className="text-xs text-zinc-600 mb-2 leading-relaxed">{task.descripcion}</p>
        )}
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge variant="outline" className={`text-[10px] ${prioridad.className}`}>
            {prioridad.label}
          </Badge>
          {task.estimacion && (
            <span className="text-[10px] text-zinc-500 font-mono">⏱ {task.estimacion}</span>
          )}
        </div>
        <div data-no-expand>
          <Select
            value={task.estado}
            onValueChange={(v) => onUpdate(task.id, { estado: v, completado: v === "hecho" })}
          >
            <SelectTrigger className="w-full h-7 text-[11px] mt-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ESTADOS.map((e) => (
                <SelectItem key={e.value} value={e.value} className="text-xs">
                  {e.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {expanded && hasDetalles && (
        <div className="bg-zinc-50 border-t border-zinc-200 px-3 py-2.5 rounded-b-md">
          <div className="text-[9px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">
            Detalle técnico
          </div>
          <pre className="text-[10px] text-zinc-700 font-mono whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto">
            {task.detalles}
          </pre>
        </div>
      )}
    </div>
  );
}

function NewTaskDialog({ section, onCreated }) {
  const [open, setOpen] = useState(false);
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [prioridad, setPrioridad] = useState("media");
  const [estimacion, setEstimacion] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!titulo.trim()) return;
    setSaving(true);
    try {
      const { data } = await createPlanTask({
        section,
        titulo: titulo.trim(),
        descripcion: descripcion.trim(),
        prioridad,
        estimacion: estimacion.trim(),
        estado: "pendiente",
        orden: 999,
        completado: false,
      });
      onCreated(data);
      setTitulo("");
      setDescripcion("");
      setEstimacion("");
      setPrioridad("media");
      setOpen(false);
      toast.success("Tarea creada");
    } catch {
      toast.error("Error al crear tarea");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-8 gap-1.5">
          <Plus className="w-3.5 h-3.5" />
          Nueva tarea
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nueva tarea — {section}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-medium text-zinc-700">Título</label>
            <Input
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              required
              autoFocus
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-700">Descripción</label>
            <Textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              rows={3}
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-zinc-700">Prioridad</label>
              <Select value={prioridad} onValueChange={setPrioridad}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORIDADES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-700">Estimación</label>
              <Input
                value={estimacion}
                onChange={(e) => setEstimacion(e.target.value)}
                placeholder="2 h / 1 día"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving}>
              {saving ? "Creando..." : "Crear"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PlanOverview({ tasks, sectionCounts, globalStats, onSelectSection }) {
  const completados = tasks.filter(
    (t) => t.section === "Etapas completadas"
  );

  const pendingSections = SECTION_ORDER.filter(
    (s) => s !== "Etapas completadas"
  )
    .map((s) => ({ section: s, ...SECTION_META[s], counts: sectionCounts[s] || { total: 0, done: 0 } }))
    .sort((a, b) => (a.orden || 99) - (b.orden || 99));

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* Cabecera con progreso global */}
      <div className="bg-white border border-zinc-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-zinc-900">
              Resumen del proyecto
            </h1>
            <p className="text-sm text-zinc-500 mt-0.5">
              Índice del plan de Finreport. Click en cualquier bloque para entrar al detalle.
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-semibold text-zinc-900 font-mono tabular-nums">
              {globalStats.pct}%
            </div>
            <div className="text-xs text-zinc-500">
              {globalStats.done} de {globalStats.total} tareas
            </div>
          </div>
        </div>
        <div className="mt-3 h-2 bg-zinc-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-500 transition-all"
            style={{ width: `${globalStats.pct}%` }}
          />
        </div>
      </div>

      {/* Lo que ya está hecho */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <h2 className="text-sm font-semibold tracking-wide uppercase text-zinc-700">
            Lo que ya está hecho
          </h2>
          <span className="text-xs text-zinc-400">
            {completados.length} hitos
          </span>
        </div>
        <button
          onClick={() => onSelectSection("Etapas completadas")}
          className="w-full text-left bg-emerald-50 border border-emerald-200 rounded-lg p-5 hover:border-emerald-400 transition-colors group"
        >
          <ul className="space-y-2">
            {completados.map((t) => (
              <li key={t.id} className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-zinc-900">
                    {t.titulo}
                  </div>
                  {t.descripcion && (
                    <div className="text-xs text-zinc-600 mt-0.5 leading-relaxed">
                      {t.descripcion}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-3 text-xs text-emerald-700 font-medium group-hover:underline flex items-center gap-1">
            Ver detalle técnico de cada hito
            <ArrowRight className="w-3 h-3" />
          </div>
        </button>
      </section>

      {/* Lo que falta — orden recomendado */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-blue-600" />
          <h2 className="text-sm font-semibold tracking-wide uppercase text-zinc-700">
            Lo que falta — orden recomendado
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {pendingSections.map((s) => {
            const Icon = s.icon || Circle;
            const pct = s.counts.total
              ? Math.round((s.counts.done / s.counts.total) * 100)
              : 0;
            return (
              <button
                key={s.section}
                onClick={() => onSelectSection(s.section)}
                className={`text-left ${s.bg} border ${s.border} rounded-lg p-4 hover:shadow-sm hover:border-zinc-400 transition-all group`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-9 h-9 rounded-md bg-white border border-zinc-200 shrink-0">
                    {s.orden ? (
                      <span className="text-sm font-semibold text-zinc-700 font-mono">
                        {s.orden}
                      </span>
                    ) : (
                      <Icon className={`w-4 h-4 ${s.color}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Icon className={`w-3.5 h-3.5 ${s.color}`} />
                      <span className="text-sm font-semibold text-zinc-900">
                        {s.label || s.section}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-600 leading-relaxed mb-2">
                      {s.resumen}
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-white rounded-full overflow-hidden border border-zinc-200">
                        <div
                          className="h-full bg-zinc-700 transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-zinc-500 font-mono tabular-nums shrink-0">
                        {s.counts.done}/{s.counts.total}
                      </span>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-zinc-900 group-hover:translate-x-0.5 transition-all shrink-0 mt-1" />
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Cómo usar el plan */}
      <section className="bg-zinc-900 text-white rounded-lg p-5">
        <h2 className="text-sm font-semibold tracking-wide uppercase mb-3">
          Cómo usar este plan
        </h2>
        <ul className="text-xs text-zinc-300 space-y-1.5 leading-relaxed">
          <li>• <span className="text-white">Click en cualquier card</span> para entrar al detalle de esa sección.</li>
          <li>• <span className="text-white">Click en una tarea</span> para desplegar la explicación técnica.</li>
          <li>• <span className="text-white">Checkbox</span> o <span className="text-white">selector de estado</span> para marcar progreso. Los cambios se guardan solos.</li>
          <li>• <span className="text-white">Buscador y filtros</span> dentro de cada sección.</li>
        </ul>
      </section>
    </div>
  );
}

function SaveBadge({ saving }) {
  if (saving) {
    return (
      <span className="text-xs flex items-center gap-1.5 text-zinc-500">
        <RotateCw className="w-3 h-3 animate-spin" />
        Guardando…
      </span>
    );
  }
  return (
    <span className="text-xs flex items-center gap-1.5 text-emerald-600">
      <CheckCircle2 className="w-3 h-3" />
      Cambios guardados
    </span>
  );
}

function ResetDialog({ open, onOpenChange, onConfirm }) {
  const [text, setText] = useState("");
  const [running, setRunning] = useState(false);
  const ok = text.trim().toUpperCase() === "RESETEAR";

  const close = (v) => {
    onOpenChange(v);
    if (!v) setText("");
  };

  const run = async () => {
    setRunning(true);
    try {
      await onConfirm();
      setText("");
      onOpenChange(false);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-rose-700 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Resetear todo el plan
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm text-zinc-700">
          <p>
            Esto <strong>borra todas las tareas</strong> (incluidas tus cambios:
            estados marcados, tareas nuevas, etc.) y vuelve a cargar el plan
            desde la documentación.
          </p>
          <p className="text-rose-700 font-medium">No se puede deshacer.</p>
          <div>
            <label className="text-xs font-medium text-zinc-700">
              Para confirmar, escribe{" "}
              <span className="font-mono text-rose-700">RESETEAR</span>:
            </label>
            <Input
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="mt-1.5 font-mono"
              placeholder="RESETEAR"
              data-testid="plan-reset-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)} disabled={running}>
            Cancelar
          </Button>
          <Button
            className="bg-rose-600 hover:bg-rose-700 text-white"
            disabled={!ok || running}
            onClick={run}
            data-testid="plan-reset-confirm"
          >
            {running ? "Recargando…" : "Borrar y recargar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlanContent() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(OVERVIEW_KEY);
  const [view, setView] = useState("list"); // list | kanban
  const [search, setSearch] = useState("");
  const [filterEstado, setFilterEstado] = useState("todos");
  const [filterPrioridad, setFilterPrioridad] = useState("todos");
  const [expanded, setExpanded] = useState(new Set());
  const [savingCount, setSavingCount] = useState(0);
  const [resetOpen, setResetOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleReset = async () => {
    try {
      await resetPlanTasks();
      setExpanded(new Set());
      const { data } = await listPlanTasks();
      setTasks(data);
      toast.success("Plan recargado");
    } catch {
      toast.error("Error al recargar");
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data: res } = await syncPlanTasks();
      const { data } = await listPlanTasks();
      setTasks(data);
      const added = res?.added ?? 0;
      if (added > 0) {
        toast.success(`Sincronizado: ${added} tarea${added === 1 ? "" : "s"} nueva${added === 1 ? "" : "s"} añadida${added === 1 ? "" : "s"}`);
      } else {
        toast.info("Plan al día — no hay tareas nuevas");
      }
    } catch {
      toast.error("Error al sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const { data } = await listPlanTasks();
      if (data.length === 0) {
        await seedPlanTasks();
        const { data: seeded } = await listPlanTasks();
        setTasks(seeded);
      } else {
        setTasks(data);
      }
    } catch (err) {
      toast.error("Error cargando plan");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const handleUpdate = async (id, patch) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, ...patch } : t))
    );
    setSavingCount((c) => c + 1);
    try {
      await updatePlanTask(id, patch);
    } catch {
      toast.error("Error al guardar");
      fetchTasks();
    } finally {
      setSavingCount((c) => Math.max(0, c - 1));
    }
  };

  const handleDelete = async (id) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
    setSavingCount((c) => c + 1);
    try {
      await deletePlanTask(id);
      toast.success("Tarea borrada");
    } catch {
      toast.error("Error al borrar");
      fetchTasks();
    } finally {
      setSavingCount((c) => Math.max(0, c - 1));
    }
  };

  const sections = useMemo(() => {
    const known = new Set(SECTION_ORDER);
    const fromTasks = Array.from(new Set(tasks.map((t) => t.section)));
    const extras = fromTasks.filter((s) => !known.has(s));
    return [...SECTION_ORDER, ...extras];
  }, [tasks]);

  const sectionCounts = useMemo(() => {
    const out = {};
    for (const s of sections) {
      const items = tasks.filter((t) => t.section === s);
      const done = items.filter((t) => t.completado || t.estado === "hecho").length;
      out[s] = { total: items.length, done };
    }
    return out;
  }, [tasks, sections]);

  const filteredTasks = useMemo(() => {
    return tasks
      .filter((t) => t.section === activeSection)
      .filter((t) => {
        if (filterEstado !== "todos" && t.estado !== filterEstado) return false;
        if (filterPrioridad !== "todos" && t.prioridad !== filterPrioridad) return false;
        if (search) {
          const q = search.toLowerCase();
          return (
            (t.titulo || "").toLowerCase().includes(q) ||
            (t.descripcion || "").toLowerCase().includes(q)
          );
        }
        return true;
      })
      .sort((a, b) => (a.orden || 0) - (b.orden || 0));
  }, [tasks, activeSection, filterEstado, filterPrioridad, search]);

  const globalStats = useMemo(() => {
    const total = tasks.length;
    const done = tasks.filter((t) => t.completado || t.estado === "hecho").length;
    return { total, done, pct: total ? Math.round((done / total) * 100) : 0 };
  }, [tasks]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <RotateCw className="w-5 h-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {/* Sidebar */}
      <aside className="w-72 bg-white border-r border-zinc-200 flex flex-col">
        <div className="px-5 py-5 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-zinc-900 flex items-center justify-center">
              <ListChecks className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-sm font-semibold tracking-tight text-zinc-900">
                Finreport / Plan
              </h1>
              <p className="text-[10px] text-zinc-500">Seguimiento interno</p>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between text-xs">
            <span className="text-zinc-500">Progreso global</span>
            <span className="font-mono text-zinc-900">
              {globalStats.done}/{globalStats.total} · {globalStats.pct}%
            </span>
          </div>
          <div className="mt-1.5 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${globalStats.pct}%` }}
            />
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
          <button
            onClick={() => setActiveSection(OVERVIEW_KEY)}
            className={`w-full text-left px-3 py-2 rounded-md text-xs flex items-center gap-2 transition-colors mb-2 ${
              activeSection === OVERVIEW_KEY
                ? "bg-zinc-900 text-white"
                : "text-zinc-700 hover:bg-zinc-100"
            }`}
            data-testid="section-overview"
          >
            <LayoutDashboard className="w-3.5 h-3.5" />
            <span className="font-semibold">Resumen</span>
          </button>

          <div className="text-[9px] font-semibold uppercase tracking-wider text-zinc-400 px-3 pt-2 pb-1">
            Secciones
          </div>

          {sections.map((s) => {
            const counts = sectionCounts[s] || { total: 0, done: 0 };
            const active = s === activeSection;
            return (
              <button
                key={s}
                onClick={() => setActiveSection(s)}
                className={`w-full text-left px-3 py-2 rounded-md text-xs flex items-center justify-between transition-colors ${
                  active
                    ? "bg-zinc-900 text-white"
                    : "text-zinc-700 hover:bg-zinc-100"
                }`}
                data-testid={`section-${s}`}
              >
                <span className="truncate pr-2">{SECTION_META[s]?.label || s}</span>
                <span
                  className={`font-mono text-[10px] ${
                    active ? "text-zinc-300" : "text-zinc-500"
                  }`}
                >
                  {counts.done}/{counts.total}
                </span>
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-zinc-200 space-y-1">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-xs text-zinc-600"
            onClick={() => {
              lockPlan();
              window.location.reload();
            }}
          >
            <LogOut className="w-3.5 h-3.5 mr-2" />
            Bloquear
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-xs text-zinc-600"
            onClick={handleSync}
            disabled={syncing}
            data-testid="plan-sync-trigger"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sincronizando…" : "Sincronizar nuevas tareas"}
          </Button>
          <button
            type="button"
            onClick={() => setResetOpen(true)}
            className="w-full text-left text-[10px] text-zinc-400 hover:text-rose-600 px-3 py-1 transition-colors"
            data-testid="plan-reset-trigger"
          >
            Resetear plan…
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {activeSection === OVERVIEW_KEY ? (
          <>
            <header className="bg-white border-b border-zinc-200 px-6 py-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold tracking-tight text-zinc-900">
                Resumen
              </h2>
              <SaveBadge saving={savingCount > 0} />
            </header>
            <main className="flex-1 overflow-y-auto p-6 bg-zinc-50">
              <PlanOverview
                tasks={tasks}
                sectionCounts={sectionCounts}
                globalStats={globalStats}
                onSelectSection={setActiveSection}
              />
            </main>
          </>
        ) : (
        <>
        {/* Topbar */}
        <header className="bg-white border-b border-zinc-200 px-6 py-3 flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <h2 className="text-lg font-semibold tracking-tight text-zinc-900">
              {SECTION_META[activeSection]?.label || activeSection}
            </h2>
            <p className="text-xs text-zinc-500">
              {sectionCounts[activeSection]?.done || 0} de{" "}
              {sectionCounts[activeSection]?.total || 0} tareas completadas
            </p>
          </div>

          <div className="relative w-56">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 text-xs"
            />
          </div>

          <Select value={filterEstado} onValueChange={setFilterEstado}>
            <SelectTrigger className="w-[130px] h-8 text-xs">
              <Filter className="w-3 h-3 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos" className="text-xs">Todos los estados</SelectItem>
              {ESTADOS.map((e) => (
                <SelectItem key={e.value} value={e.value} className="text-xs">
                  {e.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterPrioridad} onValueChange={setFilterPrioridad}>
            <SelectTrigger className="w-[120px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos" className="text-xs">Toda prioridad</SelectItem>
              {PRIORIDADES.map((p) => (
                <SelectItem key={p.value} value={p.value} className="text-xs">
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex border border-zinc-200 rounded-md overflow-hidden">
            <button
              onClick={() => setView("list")}
              className={`h-8 px-2.5 text-xs flex items-center gap-1.5 ${
                view === "list"
                  ? "bg-zinc-900 text-white"
                  : "bg-white text-zinc-600 hover:bg-zinc-50"
              }`}
              data-testid="view-list"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              Lista
            </button>
            <button
              onClick={() => setView("kanban")}
              className={`h-8 px-2.5 text-xs flex items-center gap-1.5 ${
                view === "kanban"
                  ? "bg-zinc-900 text-white"
                  : "bg-white text-zinc-600 hover:bg-zinc-50"
              }`}
              data-testid="view-kanban"
            >
              <KanbanSquare className="w-3.5 h-3.5" />
              Kanban
            </button>
          </div>

          <NewTaskDialog
            section={activeSection}
            onCreated={(t) => setTasks((prev) => [...prev, t])}
          />

          <SaveBadge saving={savingCount > 0} />
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {filteredTasks.length === 0 ? (
            <div className="text-center py-20 text-zinc-400 text-sm">
              No hay tareas que coincidan con los filtros.
            </div>
          ) : view === "list" ? (
            <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
              {filteredTasks.map((t) => (
                <TaskRow
                  key={t.id}
                  task={t}
                  expanded={expanded.has(t.id)}
                  onToggleExpand={toggleExpand}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {ESTADOS.map((col) => {
                const colTasks = filteredTasks.filter((t) => t.estado === col.value);
                const Icon = col.icon;
                return (
                  <div key={col.value} className="bg-zinc-100 rounded-md p-3 min-h-[200px]">
                    <div className="flex items-center gap-2 mb-3 px-1">
                      <Icon className={`w-4 h-4 ${col.color}`} />
                      <span className="text-xs font-semibold text-zinc-700 uppercase tracking-wide">
                        {col.label}
                      </span>
                      <span className="text-xs text-zinc-500 font-mono ml-auto">
                        {colTasks.length}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {colTasks.map((t) => (
                        <KanbanCard
                          key={t.id}
                          task={t}
                          expanded={expanded.has(t.id)}
                          onToggleExpand={toggleExpand}
                          onUpdate={handleUpdate}
                          onDelete={handleDelete}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
        </>
        )}
      </div>

      <ResetDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        onConfirm={handleReset}
      />
    </div>
  );
}

export default function PlanPage() {
  const [unlocked, setUnlocked] = useState(() => isPlanUnlocked());

  if (!unlocked) return <PasswordGate onUnlock={() => setUnlocked(true)} />;
  return <PlanContent />;
}
